import type { CmmsField, CmmsVendor } from "@assetwatch/marketplace-api";
import {
  Alert,
  Card,
  Chip,
  Divider,
  EmptyState,
  Group,
  Icon,
  Input,
  Select,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCmmsVendors } from "../queries/useCmmsVendors";

/**
 * Read-only CMMS setup preview.
 *
 * Renders each vendor's real credential schema, pulled live from
 * cmms_field_definitions.py via the BFF. Nothing here writes: no credential is
 * submitted, no secret created, no Facility_Service row touched. The inputs
 * exist so the real field set, ordering, grouping, and help text can be
 * reviewed against an actual vendor.
 *
 * Used by CmmsIntegrationAccordion (Marketplace home) and by the
 * /capabilities/$capabilityId deep-link route.
 */

const BENEFIT_KEYS = [
  "marketplace.cmms.benefitWorkOrders",
  "marketplace.cmms.benefitSync",
  "marketplace.cmms.benefitFeedback",
] as const;

/** Fields with no `uiGroup` still need a home, so they fall into one section. */
const UNGROUPED_SECTION_KEY = "__ungrouped__";

function groupFields(
  vendor: CmmsVendor,
  labels: { ungrouped: string; other: string },
) {
  const sections =
    vendor.uiSections.length > 0
      ? vendor.uiSections
      : [{ key: UNGROUPED_SECTION_KEY, label: labels.ungrouped }];

  const grouped = sections.map((section) => ({
    ...section,
    fields: vendor.fields.filter((field) =>
      section.key === UNGROUPED_SECTION_KEY
        ? true
        : field.uiGroup === section.key,
    ),
  }));

  // A field whose uiGroup matches no declared section would otherwise vanish.
  const placed = new Set(
    grouped.flatMap((section) => section.fields.map((f) => f.inputName)),
  );
  const orphans = vendor.fields.filter((f) => !placed.has(f.inputName));

  return [
    ...grouped.filter((section) => section.fields.length > 0),
    ...(orphans.length > 0
      ? [{ key: "__other__", label: labels.other, fields: orphans }]
      : []),
  ];
}

function FieldControl({ field }: { field: CmmsField }) {
  const { t } = useTranslation();
  const label = field.label || field.inputName;
  const tooltip = field.tooltip ?? undefined;
  const placeholder = field.placeholder ?? undefined;

  if (field.options && field.options.length > 0) {
    return (
      <Select
        label={label}
        options={field.options.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        placeholder={placeholder ?? t("marketplace.cmms.selectOption")}
        required={field.required}
        tooltip={tooltip}
      />
    );
  }

  const inputType =
    field.inputType === "password"
      ? "password"
      : field.inputType === "int"
        ? "number"
        : field.inputType === "url"
          ? "url"
          : "text";

  return (
    <Input
      label={label}
      placeholder={placeholder}
      required={field.required}
      tooltip={tooltip}
      type={inputType}
    />
  );
}

function VendorFields({ vendor }: { vendor: CmmsVendor }) {
  const { t } = useTranslation();
  const sections = groupFields(vendor, {
    ungrouped: t("marketplace.cmms.connectionDetails"),
    other: t("marketplace.cmms.otherFields"),
  });

  return (
    <Stack gap={24}>
      {sections.map((section, index) => (
        <Stack gap={16} key={section.key}>
          {index > 0 ? <Divider /> : null}
          <Group gap={8} justify="between" wrap="wrap">
            <Title as="h4" size="sm">
              {section.label}
            </Title>
            <Chip
              label={t("marketplace.cmms.fieldCount", {
                count: section.fields.length,
              })}
              tone="neutral"
            />
          </Group>
          <Stack gap={16}>
            {section.fields.map((field) => (
              <FieldControl field={field} key={field.inputName} />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

export function CmmsIntegrationPanel() {
  const { t } = useTranslation();
  const { data, isPending, isError } = useCmmsVendors();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const vendors = data?.vendors ?? [];
  const selected = vendors.find(
    (vendor) => String(vendor.cmmsId) === selectedId,
  );

  if (isPending) {
    return (
      <Stack gap={16}>
        <Skeleton height={64} />
        <Skeleton height={180} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert
        subtext={t("common.refreshAndTryAgain")}
        title={t("marketplace.cmms.loadError")}
        tone="negative"
      />
    );
  }

  if (vendors.length === 0) {
    return (
      <EmptyState
        description={t("marketplace.cmms.noVendorsDescription")}
        icon="service"
        title={t("marketplace.cmms.noVendorsTitle")}
      />
    );
  }

  return (
    <Stack gap={20}>
      <Text tone="subtle">{t("marketplace.cmms.intro")}</Text>

      <Card padding="comfortable">
        <Stack gap={12}>
          <Title as="h4" size="sm">
            {t("marketplace.cmms.benefitsTitle")}
          </Title>
          <Stack gap={10}>
            {BENEFIT_KEYS.map((benefitKey) => (
              <Group align="start" gap={10} key={benefitKey} wrap="nowrap">
                <Icon
                  aria-hidden
                  name="circle-check"
                  size="sm"
                  tone="positive"
                />
                <Text>{t(benefitKey)}</Text>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card padding="comfortable">
        <Stack gap={20}>
          <Stack gap={4}>
            <Title as="h4" size="sm">
              {t("marketplace.cmms.connectTitle")}
            </Title>
            <Text tone="subtle">
              {t("marketplace.cmms.providers", { count: vendors.length })}
            </Text>
          </Stack>

          <Select
            label={t("marketplace.cmms.providerLabel")}
            onChange={(value) => setSelectedId(value)}
            options={vendors.map((vendor) => ({
              value: String(vendor.cmmsId),
              label: vendor.name,
            }))}
            placeholder={t("marketplace.cmms.providerPlaceholder")}
            value={selectedId}
          />

          {selected ? (
            <Stack gap={20}>
              <Divider />
              <Stack gap={4}>
                <Title as="h4" size="sm">
                  {t("marketplace.cmms.configureTitle", {
                    vendorName: selected.name,
                  })}
                </Title>
                <Text tone="subtle">
                  {t("marketplace.cmms.configureDescription", {
                    vendorName: selected.name,
                  })}
                </Text>
              </Stack>
              <VendorFields vendor={selected} />
            </Stack>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
